import React, { useCallback, useEffect, useState } from "react";
import { Badge, Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BreadcrumbItem from "../../../shared/components/BreadcrumbItem";
import GoogleMapComponent, {
  IMapMarker,
} from "../../../shared/components/GoogleMapComponent";
import { handleError } from "../../../shared/functions/handleError";
import {
  IIncident,
  IIncidentActivity,
} from "../../../shared/interfaces/incident/IIncident";
import { ExternalIncidentService } from "../service";
import { useIncidentItemWebSocket } from "../../../shared/hooks/useWebSocket";
import { useNewActivityAlert } from "../../../shared/hooks/useNewActivityAlert";
import ActivityIcon from "../../../shared/components/ActivityIcon";
import { getActivityDisplay } from "../../../shared/functions/incidentDisplay";
import {
  IRawActivityPayload,
  isActivityChangeSignal,
  prependActivity,
  sortActivitiesNewestFirst,
  toIncidentActivity,
} from "../../../shared/functions/incidentActivity";
import { instance } from "../../../utils/api/api.service";
import { EXTERNAL_INCIDENT_ACTIVITIES_URL } from "../../../utils/api/endpoints";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

const getStatusBadge = (status: string) => {
  switch (status) {
    case "PENDING":
      return "warning";
    case "IN_PROGRESS":
      return "info";
    case "RESOLVED":
      return "success";
    case "CANCELLED":
    case "REVOKED":
      return "secondary";
    default:
      return "primary";
  }
};

const PoliceIncidentDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { incidentId } = useParams();
  const { userData } = useSelector((state: RootState) => state.auth);
  const [incident, setIncident] = useState<IIncident | null>(null);
  const [activities, setActivities] = useState<IIncidentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [contentModal, setContentModal] = useState<{
    show: boolean;
    loading: boolean;
    url: string | null;
    mediaType: string | null;
  }>({ show: false, loading: false, url: null, mediaType: null });
  const handleOpenContent = (activity: IIncidentActivity) => {
    const contentUrl = `${EXTERNAL_INCIDENT_ACTIVITIES_URL}/${activity.id}/content`;
    setContentModal({
      show: true,
      loading: true,
      url: null,
      mediaType: activity.mediaType || null,
    });

    instance
      .get(contentUrl, { responseType: "blob" })
      .then((response) => {
        const blobUrl = window.URL.createObjectURL(response.data);
        setContentModal((prev) => ({ ...prev, loading: false, url: blobUrl }));
      })
      .catch((error) => {
        handleError(error);
        setContentModal({
          show: false,
          loading: false,
          url: null,
          mediaType: null,
        });
      });
  };

  const handleCloseContent = () => {
    if (contentModal.url) {
      window.URL.revokeObjectURL(contentModal.url);
    }
    setContentModal({
      show: false,
      loading: false,
      url: null,
      mediaType: null,
    });
  };

  useEffect(() => {
    if (!incidentId) return;

    Promise.all([
      ExternalIncidentService.getSingle(Number(incidentId)),
      ExternalIncidentService.getActivities(Number(incidentId)),
    ])
      .then(([singleRes, activitiesRes]) => {
        setIncident(singleRes?.data || null);
        setActivities(activitiesRes?.data || []);
      })
      .catch(handleError)
      .finally(() => setIsLoading(false));
  }, [incidentId]);

  const handleActivityReceived = useCallback(
    (rawActivity: IRawActivityPayload) => {
      if (isActivityChangeSignal(rawActivity)) {
        if (!incidentId) return;
        Promise.all([
          ExternalIncidentService.getSingle(Number(incidentId)),
          ExternalIncidentService.getActivities(Number(incidentId)),
        ])
          .then(([singleRes, activitiesRes]) => {
            setIncident(singleRes?.data || null);
            setActivities(activitiesRes?.data || []);
          })
          .catch(handleError);
        return;
      }
      const fallbackUserName =
        `${userData?.firstName ?? ""} ${userData?.lastName ?? ""}`.trim();
      const newActivity = toIncidentActivity(rawActivity, fallbackUserName);
      setActivities((prev) => prependActivity(prev, newActivity));
    },
    [userData, incidentId],
  );

  const newActivityIds = useNewActivityAlert(
    incidentId,
    isLoading ? undefined : activities,
  );

  useIncidentItemWebSocket(
    incidentId ? Number(incidentId) : undefined,
    handleActivityReceived,
  );

  const formatActivityDate = (dateString?: string): string => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "-" : d.toLocaleString();
  };

  const activityMarkers: IMapMarker[] = activities
    .filter((a) => a.latitude && a.longitude)
    .map((a) => ({
      lat: a.latitude!,
      lng: a.longitude!,
      title: a.userFullName,
      note: getActivityDisplay(a, t, incident?.incidentType).message,
      subtitle: formatActivityDate(a.createdAt),
    }));

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="ph-duotone ph-warning f-48 mb-3 d-block" />
        <p>{t("general.notAvailable")}</p>
      </div>
    );
  }

  const isDrill = incident.drillMode;

  return (
    <React.Fragment>
      <BreadcrumbItem
        mainTitle={t("policeDashboard.title")}
        subTitle={t("incident.viewIncident")}
        buttonTitle={t("button.back")}
        buttonIcon="ti ti-arrow-left"
        onCtaClick={() => navigate("/dashboard")}
      />


      {/* Map + Timeline (left) + Emergency Card (right) */}
      <Row>
        {/* Left Column: Map + Activity Timeline */}
        <Col md={8}>
          {/* Map Card */}
          <Card className="mb-3">
            <Card.Body className="p-0">
              <GoogleMapComponent
                latitude={incident.latitude}
                longitude={incident.longitude}
                height="250px"
                zoom={17}
                markers={activityMarkers}
                mainLabel={t("incident.incidentLocation")}
              />
            </Card.Body>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <Card.Header>
              <h5>{t("incident.activityTimeline")}</h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: "500px", overflowY: "auto" }}>
              <div className="d-flex flex-column gap-3">
                {activities.length > 0 ? (
                  sortActivitiesNewestFirst(activities).map((activity) => {
                    const { icon, imageSrc, message } = getActivityDisplay(
                        activity,
                        t,
                        incident?.incidentType,
                      );
                    const isClickable =
                      activity.mediaType === "IMAGE" ||
                      activity.mediaType === "VIDEO" ||
                      activity.mediaType === "VOICE_RECORD";
                    return (
                      <div
                        key={activity.id}
                        className={`d-flex align-items-start gap-3 p-3 rounded ${
                            newActivityIds.has(activity.id)
                              ? "activity-item--new"
                              : "bg-light"
                          }`}
                      >
                        <div
                          className={`avtar avtar-m flex-shrink-0 ${
                              imageSrc ? "activity-icon--incident" : "bg-light-secondary"
                            }`}
                          style={
                            isClickable ? { cursor: "pointer" } : undefined
                          }
                          onClick={
                            isClickable
                              ? () => handleOpenContent(activity)
                              : undefined
                          }
                          role={isClickable ? "button" : undefined}
                          title={
                            isClickable ? t("incident.clickToView") : undefined
                          }
                        >
                          <ActivityIcon icon={icon} imageSrc={imageSrc} />
                        </div>
                        <div
                          className="flex-grow-1"
                          style={
                            isClickable ? { cursor: "pointer" } : undefined
                          }
                          onClick={
                            isClickable
                              ? () => handleOpenContent(activity)
                              : undefined
                          }
                          role={isClickable ? "button" : undefined}
                        >
                          <p className="mb-0">
                            <small className="text-muted">
                              {formatActivityDate(activity.createdAt)}
                            </small>
                          </p>
                          <p className="mb-0">
                            <strong>{activity.userFullName}</strong> {message}
                          </p>
                          {isClickable && (
                            <small className="text-primary mt-1 d-block">
                              <i className="ti ti-eye me-1" />
                              {t("incident.clickToView")}
                            </small>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4">
                    <i className="ti ti-list f-40 text-muted mb-2" />
                    <p className="text-muted">{t("incident.noActivityYet")}</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column: Emergency Summary Card */}
        <Col md={4}>
          <Card
            className={isDrill ? "border-warning" : "border-danger"}
            style={{ borderWidth: isDrill ? "2px" : "3px" }}
          >
            <Card.Header
              className={
                isDrill ? "bg-warning bg-opacity-25" : "bg-danger bg-opacity-25"
              }
            >
              <h5
                className={
                  isDrill
                    ? "mb-0 text-warning-emphasis"
                    : "mb-0 text-danger-emphasis"
                }
              >
                {t(`incident.type.${incident.incidentType}`, {
                  defaultValue: incident.incidentType,
                })}{" "}
                {t("incident.isInProgress")}
              </h5>
            </Card.Header>
            <Card.Body
              className={
                isDrill ? "bg-warning bg-opacity-10" : "bg-danger bg-opacity-10"
              }
            >
              <Row>
                {/* Left side - Incident Details */}
                <Col xs={7}>
                  <div className="mb-3">
                    <h6 className="mb-2">{t("incident.reportedBy")}</h6>
                    <p className="mb-0">
                      {incident.userFullName || t("general.notAvailable")}
                    </p>
                  </div>

                  <div className="mb-3">
                    <h6 className="mb-2">{t("general.location")}</h6>
                    <p className="mb-0">
                      {incident.institutionName || t("general.notAvailable")}
                    </p>
                  </div>

                  <div className="mb-0">
                    <h6 className="mb-2">{t("incident.reportedDate")}</h6>
                    <p className="mb-0">
                      {incident.createdAt
                        ? new Date(incident.createdAt).toLocaleString()
                        : t("general.notAvailable")}
                    </p>
                  </div>
                </Col>

                {/* Right side - Incident Type Icon */}
                <Col
                  xs={5}
                  className="d-flex align-items-center justify-content-center"
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: "120px",
                      height: "120px",
                      backgroundColor: "#d21034",
                    }}
                  >
                    <img
                      src={`/assets/images/${incident.incidentType.toLowerCase()}.png`}
                      alt={t(`incident.type.${incident.incidentType}`)}
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Content Viewer Modal */}
      <Modal
        show={contentModal.show}
        onHide={handleCloseContent}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {contentModal.mediaType === "IMAGE"
              ? t("incident.imagePreview")
              : contentModal.mediaType === "VIDEO"
                ? t("incident.videoPreview")
                : t("incident.audioPreview")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0">
          {contentModal.loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : contentModal.url ? (
            contentModal.mediaType === "VIDEO" ? (
              <video
                src={contentModal.url}
                controls
                autoPlay
                style={{ width: "100%", maxHeight: "70vh" }}
              />
            ) : contentModal.mediaType === "VOICE_RECORD" ? (
              <div className="p-4">
                <div className="d-flex flex-column align-items-center gap-3">
                  <div className="avtar avtar-l bg-light-primary rounded-circle">
                    <i className="ti ti-volume f-36" />
                  </div>
                  <audio
                    src={contentModal.url}
                    controls
                    autoPlay
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            ) : (
              <img
                src={contentModal.url}
                alt={t("incident.imagePreview")}
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            )
          ) : null}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default PoliceIncidentDetail;
